
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageDropzone } from "./ImageDropzone";
import type { SectionBackground, BackgroundSize, BackgroundPosition, BackgroundRepeat } from "./types";

interface SectionBackgroundDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  background?: SectionBackground;
  onUpdate: (background: SectionBackground | undefined) => void;
}

export const SectionBackgroundDialog = ({ 
  isOpen,
  onOpenChange,
  background,
  onUpdate 
}: SectionBackgroundDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Section Background</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Background Image</Label>
            <ImageDropzone
              value={background?.image || ""}
              onChange={(base64) => {
                if (!base64) {
                  onUpdate(undefined);
                  return;
                }
                onUpdate({
                  image: base64,
                  size: background?.size || "cover",
                  position: background?.position || "center",
                  repeat: background?.repeat || "no-repeat"
                });
              }}
            />
          </div>
          
          {background?.image && (
            <>
              <div className="space-y-2">
                <Label>Size</Label>
                <Select
                  value={background.size}
                  onValueChange={(value: BackgroundSize) => 
                    onUpdate({ ...background, size: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cover">Cover</SelectItem>
                    <SelectItem value="contain">Contain</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Position</Label>
                <Select
                  value={background.position}
                  onValueChange={(value: BackgroundPosition) => 
                    onUpdate({ ...background, position: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Repeat</Label>
                <Select
                  value={background.repeat}
                  onValueChange={(value: BackgroundRepeat) => 
                    onUpdate({ ...background, repeat: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-repeat">No Repeat</SelectItem>
                    <SelectItem value="repeat">Repeat</SelectItem>
                    <SelectItem value="repeat-x">Repeat Horizontally</SelectItem>
                    <SelectItem value="repeat-y">Repeat Vertically</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
